import React, { useEffect, useReducer, useState, useRef, useCallback } from 'react';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import ProductReservationCard from './ProductReservationCard';
import i18n from '../../../../i18n';
import OpeningTimes from '../OpeningTimes/OpeningTimes';
import StoreAddress from '../OpeningTimes/StroreAddress';
import DatePickerCustomHeader from './DatePickerCustomHeader';
import './category_reservation.css';
import useForm from '../../../../components/Form/use-form';
import useHttp from '../../../../hooks/use-http';


export const formatDateTimeForInput = (date) => {
    if (!date) return '';
    
    // Handle both Date objects and string dates
    const dateObj = date instanceof Date ? date : new Date(date);
    
    // Adjust for timezone offset
    const timezoneOffset = dateObj.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
    const localDate = new Date(dateObj.getTime() - timezoneOffset);
    
    // Format the date to YYYY-MM-DDThh:mm
    return localDate.toISOString().slice(0, 16);
  };

export const collectFormResponseErrors = (form) => {
    const messages = [];
    const seen = new Set();

    const addMessage = (message) => {
        if (!message || seen.has(message)) return;
        seen.add(message);
        messages.push(message);
    };

    const walkFields = (fields, pathPrefix = '') => {
        if (!fields || typeof fields !== 'object') return;
        Object.entries(fields).forEach(([fieldName, field]) => {
            if (!field || typeof field !== 'object') return;
            const label = field.label || fieldName;
            const path = pathPrefix ? `${pathPrefix} › ${label}` : label;
            if (Array.isArray(field.errors)) {
                field.errors.forEach((err) => addMessage(`${path}: ${err}`));
            }
            if (field.fields) {
                walkFields(field.fields, path);
            }
        });
    };

    if (form?.message) {
        addMessage(form.message);
    }
    if (Array.isArray(form?.errors)) {
        form.errors.forEach(addMessage);
    }
    if (form?.fields) {
        walkFields(form.fields);
    }
    return messages;
};

const initialState = {
    startDate: null,//new Date(new Date()),
    endDate: null//new Date(new Date() + 24 * 60 * 60 * 1000)
}

const CategoryReservation = ({category_id, overviewType = 'list'}) => {
    const productOverviewType = overviewType === 'grid' ? 'grid' : 'list'
    const [dates, setDates] = useReducer((state, newState) => {
        return newState
    }, initialState);
    const [minDate, setMinDate] = useState(null)
    const [category, setCategory] = useState(category_id)
    const [selectedGroupId, setSelectedGroupId] = useState(null)
    const isMobile = window?.innerWidth <= 980
    const datesRef = useRef(dates)
    const cartRef = useRef({})
    /** Bumps on each products fetch; stale responses (older seq) are ignored. */
    const productsRequestSeqRef = useRef(0)
    const productsAbortRef = useRef(null)
    useEffect(() => {
        datesRef.current = dates
    }, [dates])

    // Helper function to get day name from day of week (0 = Sunday, 1 = Monday, etc.)
    const getDayName = (dayOfWeek) => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days[dayOfWeek];
    }

    const formatDateKey = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const parseOpeningHoursDate = (value) => {
        if (!value) return null;
        const parsed = new Date(String(value).replace(' ', 'T'));
        return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
    };

    const isDateClosedByOpeningHours = (date, openingHours, earliestBookableDate) => {
        if (!openingHours) return false;

        const closedDates = openingHours.closed_dates;
        if (closedDates?.length) {
            const dateKey = formatDateKey(date);
            if (closedDates.includes(dateKey)) {
                return true;
            }
        }

        if (openingHours.closed_period) {
            const validFrom = parseOpeningHoursDate(openingHours.valid_from);
            const validTo = parseOpeningHoursDate(openingHours.valid_to);
            const minDay = earliestBookableDate ? startOfDay(earliestBookableDate) : null;
            const day = startOfDay(date);
            // After min_time all dates are bookable except closed_dates (handled above)
            if (minDay && day >= minDay) {
                return false;
            }
            if (validFrom && validTo && day >= validFrom && day <= validTo) {
                return true;
            }
        }

        return false;
    };

    const isAfterMinBookableDate = (date) =>
        minDate && startOfDay(date) >= startOfDay(minDate);
    
    // Function to check if a date should be disabled for pick up (start date)
    // filterDate returns true for enabled dates, false for disabled dates
    // Note: minDate and maxDate props also affect which dates are enabled
    const filterDatePickUp = (date) => {
        // First check if date is before minDate (minDate prop handles this, but we check here too for safety)
        if (minDate && date < minDate) {
            return false;
        }
        
        // If no store or opening hours, enable all dates (default behavior)
        if (!store || !store.opening_hours) {
            return true;
        }

        if (isDateClosedByOpeningHours(date, store.opening_hours, minDate)) {
            return false;
        }

        if (store.opening_hours.closed_period && isAfterMinBookableDate(date)) {
            return true;
        }
        
        const dayOfWeek = date.getDay();
        const dayName = getDayName(dayOfWeek);
        const openTimeField = `${dayName}_open_time`;
        const openTime = store.opening_hours[openTimeField];
        // Only check closed days restriction if the day is actually closed
        if (openTime === null && !store.opening_hours.pick_up_on_closed_days) {
            return false
        }
        return true; // Enable this date (day is open, or closed but pick_up_on_closed_days allows it)
    }
    
    // Function to check if a date should be disabled for drop off/return (end date)
    // filterDate returns true for enabled dates, false for disabled dates
    const filterDateDropOff = (date) => {
        if (minDate && date < minDate) {
            return false;
        }

        // If no store or opening hours, enable all dates (default behavior)
        if (!store || !store.opening_hours) {
            return true;
        }

        if (isDateClosedByOpeningHours(date, store.opening_hours, minDate)) {
            return false;
        }

        if (store.opening_hours.closed_period && isAfterMinBookableDate(date)) {
            return true;
        }
        
        const dayOfWeek = date.getDay();
        const dayName = getDayName(dayOfWeek);
        const openTimeField = `${dayName}_close_time`;
        const openTime = store.opening_hours[openTimeField];
        // Only check closed days restriction if the day is actually closed
        if (openTime === null && !store.opening_hours.return_on_closed_days) {
            return false
        }
        return true; // Enable this date (day is open, or closed but return_on_closed_days allows it)
    }
    
    
    const startFormUrl = category_id ? '/rest/product/select_availability/?from_time=' + (dates.startDate?.toISOString().split('T')[0] || '') + '&to_time=' + (dates.endDate?.toISOString().split('T')[0] || '') : null
    const [formUrl, setFormUrl] = useState(startFormUrl)
    const [store, setStore] = useState(null)
    const endDatePickerRef = useRef(null)
    
    // Check if time selection should be enabled (after store is declared)
    const enablePickUpTime = store?.reservation_settings?.enable_pick_up_time_selection || false;
    const enableDropOffTime = store?.reservation_settings?.enable_drop_off_time_selection || false;
    const showTimeSelect = enablePickUpTime || enableDropOffTime;
    const [cart, setCart] = useState({})
    const [submitErrors, setSubmitErrors] = useState([])
    useEffect(() => {
        cartRef.current = cart
    }, [cart])

    // Function to check if products and options in cart are still available
    const checkCartAvailability = useCallback((availableProducts, currentCart) => {
        const updatedCart = { ...currentCart }
        const unavailableItems = []
        let cartChanged = false
        
        // Check each product in cart
        Object.keys(updatedCart).forEach(productId => {
            const cartItem = updatedCart[productId]
            const availableProduct = availableProducts.find(p => p.id === productId)
            // Check if main product is still available
            if(cartItem.quantity <= 0) {
            }
            else if (!availableProduct || (availableProduct.available_listings !== undefined && availableProduct.available_listings < cartItem.quantity)) {
                unavailableItems.push(cartItem.name || `Product ${productId}`)
                delete updatedCart[productId]
                cartChanged = true
            } else if (cartItem.options && availableProduct?.product_options) {
                // Check availability of selected options
                const updatedOptions = { ...cartItem.options }
                let optionsChanged = false
                
                availableProduct.product_options.forEach(option => {
                    const optionValue = cartItem.options[option.id]
                    const isSelected = optionValue === true || (typeof optionValue === 'number' && optionValue > 0)
                    
                    if (isSelected) {
                        const optionQuantity = typeof optionValue === 'number' ? optionValue : 1
                        const optionListings = option.product_object?.available_listings
                        if (typeof optionListings === 'number' && optionListings < optionQuantity) {
                            unavailableItems.push(`${cartItem.name} - ${option.product_object?.name || `Option ${option.id}`}`)
                            delete updatedOptions[option.id]
                            optionsChanged = true
                        }
                    }
                })
                
                if (optionsChanged) {
                    updatedCart[productId] = {
                        ...cartItem,
                        options: updatedOptions
                    }
                    cartChanged = true
                }
            }
        })
        
        // Update cart if any items were removed
        if (cartChanged) {
            setCart(updatedCart)
            // Show warning if items were removed
            if (unavailableItems.length > 0) {
                const message = i18n.t('The following items are no longer available for the selected dates and have been removed from your cart: {{items}}', {
                    items: unavailableItems.join(', ')
                })
                alert(message) // You can replace this with a better notification component
            }
        }
    }, [])

    const applyProductsResponse = useCallback((r, meta = {}) => {
        if (
            meta.requestSeq !== undefined &&
            meta.requestSeq !== productsRequestSeqRef.current
        ) {
            return
        }
        setStore(r)
        if (r.min_time) {
            const nextMin = new Date(r.min_time)
            setMinDate((prev) =>
                prev && prev.getTime() === nextMin.getTime() ? prev : nextMin
            )
        }
        // Confirmed window always follows the API; only update state when times actually change
        // so we do not ping-pong formUrl → fetch → setDates → formUrl.
        if (r.from_time && r.to_time) {
            const start = new Date(r.from_time)
            const end = new Date(r.to_time)
            const cur = datesRef.current
            const sameStart = cur.startDate?.getTime() === start.getTime()
            const sameEnd = cur.endDate?.getTime() === end.getTime()
            if (!sameStart || !sameEnd) {
                setDates({ startDate: start, endDate: end })
            }
        }
        if (r.available_products && Object.keys(cartRef.current).length > 0) {
            checkCartAvailability(r.available_products, cartRef.current)
        }
    }, [checkCartAvailability])

    const {isLoading : loading, error : error, sendRequest : getProducts} = useHttp(applyProductsResponse)
    
    const formHandler = (form) => {
        if (form.success) {
            setSubmitErrors([])
            if (form.redirect_url) {
                window.location.href = form.redirect_url
            }
            return
        }
        setSubmitErrors(collectFormResponseErrors(form))
    }

    const {isLoading : formLoading, error : formError, sendRequest : submitFormData} = useForm(formHandler)
    useEffect( () => {
        setFormUrl('/rest/store/current/select_products/?from_time=' + (dates.startDate?.toISOString() || '') + '&to_time=' + (dates.endDate?.toISOString() || ''))
    }, [category, dates])

    useEffect( () => {
        if (!formUrl) {
            return
        }
        productsAbortRef.current?.abort()
        const ac = new AbortController()
        productsAbortRef.current = ac
        const requestSeq = ++productsRequestSeqRef.current
        const timeoutId = setTimeout(() => {
            getProducts({ url: formUrl, json: true, signal: ac.signal }, { requestSeq })
        }, 200)

        return () => {
            clearTimeout(timeoutId)
            ac.abort()
        }
    }, [formUrl, getProducts])

    var days = Math.ceil((dates.endDate - dates.startDate) / (24 * 60 * 60 * 1000)) 

    const handleSubmit = (e) => {
        e.preventDefault()
        setSubmitErrors([])
        
        // Build cart_lines array from cart state
        const cartLines = Object.keys(filteredCart).map((productId) => {
            const cartItem = cart[productId]
            const productobj = store.available_products.find(p => p.id === productId)
            
            if (!productobj || cartItem.quantity <= 0) {
                return null
            }
            
            // Build child_lines from selected options
            const childLines = []
            if (cartItem.options && productobj.product_options) {
                productobj.product_options.forEach(option => {
                    const optionValue = cartItem.options[option.id]
                    // Check if option is selected: for checkboxes it's boolean, for quantities it's > 0
                    const isSelected = optionValue === true || (typeof optionValue === 'number' && optionValue > 0)
                    
                    if (isSelected && option.product_object?.id) {
                        // Get quantity: for checkboxes use 1, for quantity inputs use the actual value
                        const quantity = typeof optionValue === 'number' ? optionValue : 1
                        childLines.push({
                            product: option.product_object.id,
                            quantity: quantity
                        })
                    }
                })
            }
            
            return {
                product: productId,
                quantity: cartItem.quantity,
                child_lines: childLines
            }
        }).filter(line => line !== null)

        if (cartLines.length === 0) {
            setSubmitErrors([i18n.t('Selecteer eerst een product voordat je kunt bevestigen.')])
            return
        }
        
        // Build JSON payload with cart_lines as a JSON string
        const formData = new FormData( 
        )
        formData.append('from_time', formatDateTimeForInput(dates.startDate))
        formData.append('to_time', formatDateTimeForInput(dates.endDate))
        formData.append('cart_lines', JSON.stringify(cartLines))
        // Send as JSON string with proper headers
        submitFormData({
            url: '/rest/cart/current/add_lines/',
            method: 'POST',
            data: formData,
        })
    }
    const getProductPrice = (key, totalAmount) => {
        var productobj = store.available_products.filter(product => product.id === key)[0]
        if (!productobj) return 0
        
        // Calculate time units
        const hours = Math.ceil((dates.endDate - dates.startDate) / (60 * 60 * 1000))
        const days = Math.ceil((dates.endDate - dates.startDate) / (24 * 60 * 60 * 1000))
        const people = cart[key]?.people || 1
        
        // Calculate base product price
        let basePrice = 0
        switch (productobj.calc_pricing_type) {
            case 'price':
                basePrice = productobj.price
                break
            case 'price_per_hour':
                basePrice = productobj.price * hours
                break
            case 'price_per_day':
                basePrice = productobj.price * days
                break
            case 'price_per_person':
                basePrice = productobj.price * people
                break
            case 'price_per_person_per_hour':
                basePrice = productobj.price * people * hours
                break
            case 'price_per_person_per_day':
                basePrice = productobj.price * people * days
                break
            case 'pt': // Percentage Total
                basePrice = (productobj.price / 100) * totalAmount
                break
            case 'pl': // Percentage Line
                basePrice = (productobj.price / 100) * totalAmount
                break
            default:
                basePrice = productobj.price
        }
        
        // Calculate option prices
        let optionPrice = 0
        if (cart[key]?.options && productobj.product_options) {
            productobj.product_options.forEach(option => {
                const optionValue = cart[key].options[option.id]
                // Check if option is selected: for checkboxes it's boolean, for quantities it's > 0
                const isSelected = optionValue === true || (typeof optionValue === 'number' && optionValue > 0)
                
                if (isSelected) {
                    // Get quantity: for checkboxes use 1, for quantity inputs use the actual value
                    const quantity = typeof optionValue === 'number' ? optionValue : 1
                    const unit = option.product_object?.unit
                    const quantityUnits = ['u', 'kg', 'l', 'm', 'y', 'h']
                    const isQuantityBased = unit && quantityUnits.includes(unit)
                    const optionQty = isQuantityBased ? quantity : 1
                    
                    switch (option.calc_pricing_type) {
                        case 'percentage_parent':
                            optionPrice += (option.price / 100) * basePrice * optionQty
                            break
                        case 'price':
                            optionPrice += option.price * optionQty
                            break
                        case 'price_per_hour':
                            optionPrice += option.price * hours * optionQty
                            break
                        case 'price_per_day':
                            optionPrice += option.price * days * optionQty
                            break
                        case 'price_per_person':
                            optionPrice += option.price * people * optionQty
                            break
                        case 'price_per_person_per_hour':
                            optionPrice += option.price * people * hours * optionQty
                            break
                        case 'price_per_person_per_day':
                            optionPrice += option.price * people * days * optionQty
                            break
                        default:
                            optionPrice += option.price * optionQty
                    }
                }
            })
        }
        
        return basePrice + optionPrice
    }

    const filteredCart = Object.fromEntries(
        Object.entries(cart).filter(([_, item]) => item.quantity > 0)
    );


    const totalAmount = Object.values(filteredCart).reduce((acc, product) => acc + (getProductPrice(product.id, acc) * product.quantity), 0)

    const totalAmountFormatted = new Intl.NumberFormat(undefined, { style: "currency", currency: "EUR" }).format(
        totalAmount,
      )

    const productGroups = Object.values(
        (store?.available_products || []).reduce((groups, product) => {
            (product.product_group_objects || []).forEach((group) => {
                if (group?.id && !groups[group.id]) {
                    groups[group.id] = group
                }
            })
            return groups
        }, {})
    )
    const visibleProducts = (store?.available_products || []).filter((product) => {
        if (product.type === 'parent') {
            return false
        }
        if (!selectedGroupId) {
            return true
        }
        return (product.product_group_objects || []).some((group) => String(group.id) === String(selectedGroupId))
    })
    const productVariants = Object.values(
        visibleProducts.reduce((groups, product) => {
            const key = product.parent_product || product.id
            if (!groups[key]) {
                groups[key] = []
            }
            groups[key].push(product)
            return groups
        }, {})
    )

    return (
    <div className='container '>
        <br></br>
        {
            <form id='reservation-form' className='reservation-form' onSubmit={handleSubmit} >
                <div className='product-selection'>
                    <div className='position-sticky' style={{top: '100px'}}>
                    {store && store.available_products.length > 0 ?
                        <>
                            <h6 className='text-center'>{i18n.t('Kies wat je wilt reserveren')}</h6>
                            {productGroups.length > 0 && (
                                <div className='product-group-filters d-flex flex-wrap gap-1 justify-content-center mb-2' role='group' aria-label={i18n.t('Product groups')}>
                                    <button
                                        type='button'
                                        className={`umr-btn umr-btn-sm ${!selectedGroupId ? 'umr-btn-primary' : 'umr-btn-outline-secondary'}`}
                                        onClick={() => setSelectedGroupId(null)}
                                    >
                                        {i18n.t('All')}
                                    </button>
                                    {productGroups.map((group) => (
                                        <button
                                            type='button'
                                            key={group.id}
                                            className={`umr-btn umr-btn-sm ${String(selectedGroupId) === String(group.id) ? 'umr-btn-primary' : 'umr-btn-outline-secondary'}`}
                                            onClick={() => setSelectedGroupId(group.id)}
                                        >
                                            {group.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className={productOverviewType === 'grid' ? 'product-selection-grid' : 'd-flex flex-column'}>
                                {productVariants.length > 0 ? productVariants.map((variants) => (
                                    <ProductReservationCard
                                        key={variants[0].parent_product || variants[0].id}
                                        product={variants[0]}
                                        variants={variants}
                                        cart={cart}
                                        setCart={setCart}
                                        overviewType={productOverviewType}
                                    />
                                )) : (
                                    <li className='list-group-item active m-auto text-center'>
                                        <h5>{i18n.t('Geen producten voor deze data')}</h5>
                                        <p>{i18n.t('Kies een andere groep')}</p>
                                    </li>
                                )}
                            </div>
                        </> : 
                        <li className='list-group-item active m-auto text-center' >
                            <h5>{i18n.t('Geen producten voor deze data')}</h5>
                            <p>{i18n.t('Kies een andere datum')}</p>
                        </li>
                    }
                    </div>
                </div>
                <div className='reservation-sidebar'>
                <div className='date-selection d-flex flex-column position-relative'>
                    <div className='d-flex flex-column' style={{top: '100px'}}>
                        <h5 className='mt-2 text-center'>{i18n.t('Kies de periode')}</h5>
                        {dates?.startDate && (() => {
                            // Common DatePicker props
                            const commonDatePickerProps = {
                                className: "form-control",
                                calendarClassName: "react-datepicker-custom",
                                wrapperClassName: "w-100",
                                isClearable: false,
                                monthsShown: isMobile ? 1 : 2,
                                renderCustomHeader: DatePickerCustomHeader,
                                timeIntervals: 15,
                                popperPlacement: isMobile ? "bottom-start" : "bottom"
                            };

                            return (
                                <div className="d-flex flex-column flex-md-row gap-2 gap-md-1">
                                    <div className="w-100">
                                        <label className="form-label small">{i18n.t('Van')}</label>
                                        <DatePicker
                                            {...commonDatePickerProps}
                                            selected={dates.startDate}
                                            onChange={(date) => {
                                                setDates({
                                                    startDate: date,
                                                    endDate: date
                                                });
                                                // When a date is selected, open the end date picker
                                                setTimeout(() => {
                                                    if (endDatePickerRef.current) {
                                                        const input = endDatePickerRef.current.input;
                                                        if (input) {
                                                            input.focus();
                                                            input.click();
                                                        }
                                                    }
                                                }, 0)
                                            }}
                                            selectsStart
                                            startDate={dates.startDate}
                                            endDate={dates.endDate}
                                            minDate={minDate || undefined}
                                            filterDate={filterDatePickUp}
                                            showTimeSelect={showTimeSelect && enablePickUpTime}
                                            dateFormat={showTimeSelect && enablePickUpTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"}
                                        />
                                    </div>
                                    <div className="w-100">
                                        <label className="form-label small">{i18n.t('Tot')}</label>
                                        <DatePicker
                                            {...commonDatePickerProps}
                                            ref={endDatePickerRef}
                                            selected={dates.endDate}
                                            onChange={(date) => {
                                                setDates({
                                                    startDate: dates.startDate,
                                                    endDate: date
                                                });
                                            }}
                                            selectsEnd
                                            startDate={dates.startDate}
                                            endDate={dates.endDate}
                                            minDate={dates.startDate || minDate}
                                            filterDate={filterDateDropOff}
                                            showTimeSelect={showTimeSelect && enableDropOffTime}
                                            dateFormat={showTimeSelect && enableDropOffTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"}
                                        />
                                    </div>
                                </div>
                            );
                        })()}
                        {store && store.reservation_settings && store.reservation_settings.reservation_warning_text && <div className='alert alert-warning mt-1 py-1 small'>{store.reservation_settings.reservation_warning_text}</div>}
                    </div>
                </div>
                <div className='reservation-summary d-flex flex-column position-relative '>
                    <div className='d-flex flex-column' style={{top: '100px'}}>
                        {dates && 
                            <>
                                <input type="hidden" name="from_time" value={formatDateTimeForInput(dates.startDate)} className="form-control input-daterange-timepicker" />
                                <input type="hidden" name="to_time" value={formatDateTimeForInput(dates.endDate)} className="form-control input-daterange-timepicker" />
                                <h5 className='mt-3'>{i18n.t('Je hebt gekozen voor {{days}}', {days: days})} {days === 1? i18n.t('dag') : i18n.t('dagen')} </h5>
                                {i18n.t('{{startDate}} - {{endDate}}', {
                                    startDate: dates.startDate?.toLocaleString(i18n.language || 'nl-NL', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    }),
                                    endDate: dates.endDate?.toLocaleString(i18n.language || 'nl-NL', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })
                                })}
                                <ul className='mt-1 mb-1 list-group'>
                                    {Object.keys(filteredCart).length > 0 ? 
                                        Object.keys(filteredCart).map((key) => {
                                            const productobj = store.available_products.find(p => p.id === key)
                                            const selectedOptions = cart[key]?.options || {}
                                            const hasOptions = productobj?.product_options && Object.keys(selectedOptions).some(optId => {
                                                const val = selectedOptions[optId]
                                                return val === true || (typeof val === 'number' && val > 0)
                                            })
                                            const unitPrice = getProductPrice(key, totalAmount)
                                            const lineTotal = unitPrice * cart[key].quantity
                                            return (
                                                cart[key].quantity > 0 && (
                                                    <li className='list-group-item' key={key}>
                                                        {cart[key].name}: {cart[key].quantity} {i18n.t('x €{{price}}', {price: unitPrice.toFixed(2)})} = €{lineTotal.toFixed(2)}
                                                        {hasOptions && (
                                                            <ul className='mt-1 mb-0 ps-3' style={{listStyle: 'none'}}>
                                                                {productobj.product_options
                                                                    .filter(opt => {
                                                                        const val = selectedOptions[opt.id]
                                                                        return val === true || (typeof val === 'number' && val > 0)
                                                                    })
                                                                    .map(opt => {
                                                                        const optValue = selectedOptions[opt.id]
                                                                        const unit = opt.product_object?.unit
                                                                        const quantityUnits = ['u', 'kg', 'l', 'm', 'y', 'h']
                                                                        const isQuantityBased = unit && quantityUnits.includes(unit)
                                                                        const displayQty = isQuantityBased && typeof optValue === 'number' ? optValue : null
                                                                        return (
                                                                            <li key={opt.id} className='small text-muted'>
                                                                                • {opt.product_object?.name || opt.test || `Option ${opt.id}`}
                                                                                {displayQty !== null && ` (${displayQty} ${unit})`}
                                                                            </li>
                                                                        )
                                                                    })}
                                                            </ul>
                                                        )}
                                                    </li>
                                                )
                                            )
                                        }) : <li className='list-group-item'>{i18n.t('Selecteer een product')}</li>}
                                    <li className='list-group-item umr-bg-primary' style={{backgroundColor: 'var(--umr-primary, #0d6efd)', color: 'white'}}>{i18n.t('Totaal: {{total}}', {total: totalAmountFormatted})}</li>
                                </ul>
                            </>
                        }
                        {(formError || submitErrors.length > 0) && (
                            <div className="alert alert-danger mt-2 mb-0" role="alert">
                                {formError && <p className="mb-0">{formError}</p>}
                                {submitErrors.length > 0 && (
                                    <ul className={`mb-0 ps-3 ${formError ? 'mt-2' : ''}`}>
                                        {submitErrors.map((err, index) => (
                                            <li key={index}>{err}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                        <button type="submit" disabled={formLoading || Object.keys(cart).length === 0} className="mt-2 px-5 btn-lg py-2 mx-auto umr-btn umr-btn-primary umr-text-dark" style={{color: 'var(--umr-dark, #212529)'}} >{i18n.t('Bevestigen')}</button>
                    </div>
                </div>
                <div className='reservation-store-info'>
                    <OpeningTimes openingHours={store?.opening_hours} />
                    {store && <StoreAddress store={store} />}
                </div>
                </div>
            </form>
        }
    </div>
    );
};


export default CategoryReservation